import React, { useState } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

import ArrowBackIcon from '../../assets/arrow-back-icon';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteOriginToDestination } from './components/origin-destination-list-item';
import LabelIcon from '../../components/label-icon';
import AngleRightIcon from '../../assets/angle-right-icon';
import CommentIcon from '../../assets/comment-icon';
import Card from '../tour/components/card';
import { API, ROUTES } from '../../helpers/constants';
import PhoneIcon from '../../assets/phone-icon';
import RatingReview from './components/rating-review';
import ReviewModal from './components/review-modal';
import useGet from '../../hook/use-get';
import { toHoursAndMinutes } from '../../helpers/time';
import TourLoadingItem from '../tour/components/loading-list-item';
import { SectionError } from '../../components/error';

const DepartureTime = ({ label, values }) => {
  return (
    <div className="space-y-3">
      <h3 className="text-left text-base font-medium">{label}</h3>
      <div className="flex flex-wrap gap-3">
        {values?.map((item) => (
          <div className="inline-flex" key={item}>
            <span className="text-base font-normal p-1 px-2 rounded bg-neutral-100">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CostWrapper = ({ cost, busType, busClass, isFetching }) => {
  return (
    <div className="bg-white p-6 border rounded-lg">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col justify-start">
          <h4 className="text-left text-base">Tarif</h4>
          {isFetching ? (
            <div className="w-28 h-8 bg-gray-200 animate-pulse rounded" />
          ) : (
            <h4 className="text-left text-xl font-bold">{cost}</h4>
          )}
        </div>
        <div className="flex flex-col justify-center items-end gap-1">
          {isFetching ? (
            <div className="w-28 h-8 bg-gray-200 animate-pulse rounded" />
          ) : (
            <span className="flex text-left text-sm text-white px-2 py-0.5 bg-primary-500 rounded-full">
              {busType}
            </span>
          )}
          {isFetching ? (
            <div className="w-28 h-8 bg-gray-200 animate-pulse rounded" />
          ) : (
            <span className="flex text-left text-sm text-white px-2 py-0.5 bg-gray-500 rounded-full">
              {busClass}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const BusAgentWrapper = ({ images, phoneCompany, isFetching }) => {
  const items = Array.isArray(images) ? images : [images];
  const imagesItems = items?.map((item) => ({
    original: item,
    thumbnail: item
  }));

  if (isFetching) {
    return <div className="w-full h-60 bg-gray-300 animate-pulse" />;
  }

  return (
    <div className="space-y-2">
      <div className="space-y-3">
        <h3 className="text-left text-xl font-semibold">Foto Bus</h3>
        <ImageGallery
          items={imagesItems}
          showFullscreenButton={false}
          showNav={false}
          showPlayButton={false}
        />
      </div>
      <a
        href={`tel:${phoneCompany}`}
        className="flex bg-[#FDCB2C] rounded-lg p-4 justify-center items-center space-x-2"
      >
        <PhoneIcon />
        <span>Hubungi Agen</span>
      </a>
    </div>
  );
};

const NearestTour = ({ destinationCityId, navigate }) => {
  const { data, isFetching, isError, refetch } = useGet({
    queryKey: 'nearestTours',
    api: `${API.nearestTour}/${destinationCityId}`,
    options: {
      enabled: !!destinationCityId
    }
  });

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="grid grid-cols-1 w-full mx-auto md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array(3)
            .fill(0)
            .map(() => (
              <TourLoadingItem />
            ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="h-44 items-center justify-center flex">
          <SectionError refetch={refetch} />
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 w-full mx-auto md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data?.map((item) => (
          <Card
            key={item?.['city_id']}
            name={item?.name}
            sourceImage={item?.['file_url']}
            onClick={() => navigate(`${ROUTES.tour}/${item?.id}`)}
            location={item?.['city_name']}
            capacity={item?.kapasitas}
            facilities={item?.facility}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="lg:bg-white p-0 pt-6 md:mt-0 md:p-5 lg:border rounded-lg space-y-3">
      <div className="flex flex-col lg:flex-row justify-center lg:justify-between items-center">
        <h3 className="text-center md:text-left text-base md:text-xl font-bold">
          Pilihan Wisata Terdekat
        </h3>
        <div
          className="my-2 flex lg:flex-row items-center gap-2 rounded-lg text-sm px-5 py-2.5 focus:outline-none cursor-pointer"
          onClick={() => navigate(ROUTES.tour)}
        >
          <span className={`text-[#0AB7F1] font-medium text-sm`}>Cek Selengkapnya</span>
          <AngleRightIcon color={'#0AB7F1'} />
        </div>
      </div>
      {renderContent()}
    </div>
  );
};

const TransScheduleDetails = () => {
  const navigate = useNavigate();
  const { transId, ruteId } = useParams();
  const [isShowReviewModal, setIsShowReviewModal] = useState(false);
  const [isShowCreateReviewForm, setIsShowCreateReviewForm] = useState(false);

  const { data: transportDetails, isFetching: isFetchingDetails } = useGet({
    queryKey: 'transportDetails',
    api: `${API.transportDetails}/${transId}/rute/${ruteId}`
  });

  return (
    <div className="bg-neutral-50 py-6">
      {isShowReviewModal && (
        <ReviewModal
          companyName={transportDetails?.name}
          busType={transportDetails?.mnemonic}
          busClass={transportDetails?.['class_name']}
          onBack={() => {
            setIsShowReviewModal(false);
            setIsShowCreateReviewForm(false);
          }}
          origin={{
            city: transportDetails?.['initial_city'],
            name: transportDetails?.['initial_name']
          }}
          destination={{
            city: transportDetails?.['destination_city'],
            name: transportDetails?.['destination_name']
          }}
          transportId={transportDetails?.id}
          isCreateReviewForm={isShowCreateReviewForm}
          rateAverage={transportDetails?.rate}
          totalRate={transportDetails?.['total_rate']}
        />
      )}
      <div className="w-[90%] lg:w-[70%] m-auto space-y-3">
        <div
          className="flex items-start gap-4 cursor-pointer lg:items-center"
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon />
          <span className="flex flex-col lg:flex-row gap-2 lg:gap-4 item-start lg:items-center flex-1">
            {isFetchingDetails ? (
              <>
                <div className="h-10 bg-gray-200 w-40 animate-pulse mb-2 md:mb-0" />
                <div className="h-10 bg-gray-200 w-40 animate-pulse" />
              </>
            ) : (
              <>
                <img src={transportDetails?.['company_logo_url']} alt="-" className="max-h-16 w-full max-w-[116px] object-cover" />
                <h3 className="text-xl font-bold text-left">{transportDetails?.name}</h3>
              </>
            )}
          </span>
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full lg:w-[65%] space-y-3">
            <div className="bg-white p-6 border rounded-lg">
              <RouteOriginToDestination
                origin={{
                  city: transportDetails?.['initial_city'],
                  name: transportDetails?.['initial_name']
                }}
                destination={{
                  city: transportDetails?.['destination_city'],
                  name: transportDetails?.['destination_name']
                }}
                travelTime={toHoursAndMinutes(transportDetails?.['estimated_travel_time'])}
                isFetching={isFetchingDetails}
              />
            </div>
            <div className="lg:hidden">
              <CostWrapper
                cost={transportDetails?.tariff}
                busType={transportDetails?.mnemonic}
                busClass={transportDetails?.['class_name']}
                isFetching={isFetchingDetails}
              />
            </div>
            <div className="bg-white p-6 border rounded-lg space-y-3">
              <h3 className="text-left text-xl font-bold">Lintasan Trayek</h3>
              {isFetchingDetails ? (
                <>
                  <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                  <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                </>
              ) : (
                <p className="text-left text-base">{transportDetails?.track}</p>
              )}
            </div>
            <div className="lg:hidden">
              <BusAgentWrapper
                images={transportDetails?.['vehicle_photo_url']}
                phoneCompany={transportDetails?.['phone_company']}
                isFetching={isFetchingDetails}
              />
            </div>
            <div className="bg-white p-6 border rounded-lg space-y-6">
              <h2 className="text-left text-xl font-bold">Fasilitas</h2>
              <div className="md:grid md:grid-cols-3 gap-4 space-y-3 md:space-y-0">
                {isFetchingDetails ? (
                  <>
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    {transportDetails?.facility?.map((item) => (
                      <LabelIcon
                        key={item?.name}
                        label={item?.name}
                        icon={item?.icon}
                        className="flex text-base items-center gap-4"
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="bg-white p-6 border rounded-lg space-y-6">
              <h2 className="text-left text-xl font-bold">Jadwal Keberangkatan</h2>
              <div className="space-y-5">
                {isFetchingDetails ? (
                  <>
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="w-full h-8 bg-gray-200 animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    <DepartureTime
                      label={`Dari ${transportDetails?.['initial_city']}`}
                      values={transportDetails?.jadwal?.['initial_city']}
                    />
                    <DepartureTime
                      label={`Dari ${transportDetails?.['destination_city']}`}
                      values={transportDetails?.jadwal?.['destination_city']}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="bg-white p-6 border rounded-lg">
              <RatingReview
                onClickMoreButtonBottom={() => setIsShowReviewModal(true)}
                id={transportDetails?.['id']}
                rateAverage={transportDetails?.rate}
                totalRate={transportDetails?.['total_rate']}
                isShowAll={false}
              />
            </div>
            <div
              onClick={() => {
                setIsShowReviewModal(true);
                setIsShowCreateReviewForm(true);
              }}
              className="bg-[#F2FAF7] flex p-4 border border-[#ACDEC7] rounded-lg justify-center items-center gap-3 cursor-pointer"
            >
              <CommentIcon />
              <h2 className="text-left text-base text-primary-500 font-bold">
                Buat Ulasan Angkutan ini
              </h2>
            </div>
          </div>
          <div className="hidden lg:block lg:w-[35%] space-y-3">
            <CostWrapper
              cost={transportDetails?.tariff}
              busType={transportDetails?.mnemonic}
              busClass={transportDetails?.['class_name']}
              isFetching={isFetchingDetails}
            />
            <BusAgentWrapper
              images={transportDetails?.['vehicle_photo_url']}
              isFetching={isFetchingDetails}
            />
          </div>
        </div>
        <NearestTour
          navigate={navigate}
          destinationCityId={transportDetails?.['destination_city_id']}
        />
      </div>
    </div>
  );
};

export default TransScheduleDetails;
